// Routes
export const COLLATERAL_ROUTES = {
    guarantees: '/collateral/guarantees',
    guaranteeEdit: (id: number) => `/collateral/guarantees/${id}/edit`,
    guaranteeView: (id: number) => `/collateral/guarantees/${id}/view`,
    validation: '/collateral/validation',
    validationEdit: (id: number) => `/collateral/validation/${id}/edit`,
    receivables: (validationId: number) => `/collateral/validation/${validationId}/receivables`,
} as const;

export const COLLATERAL_BREADCRUMBS = {
    structuring: { label: 'Estruturação', url: '/structuring' },
    collateral: { label: 'Garantia', url: COLLATERAL_ROUTES.guarantees },
    guarantees: { label: 'Análise de Garantias', url: COLLATERAL_ROUTES.guarantees },
    validation: { label: 'Validação', url: COLLATERAL_ROUTES.validation },
    receivables: { label: 'Recebíveis', url: '#' },
} as const;

// Formatters
export const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

export const formatPercentage = (value: number): string => `${value.toFixed(2)}%`;

export const formatDate = (value: string): string => {
    if (!value) return '';
    return new Date(value).toLocaleDateString('pt-BR');
};

export const formatCpfCnpj = (value: string): string => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return value;
};

// --- GUARANTEE (Checagem Documental) ---

export type DocStatus = 'OBRIGATORIO' | 'SIM' | 'SIM, ANTES' | 'NÃO' | 'CONTRATO' | 'N/A' | '';
export type SefazStatus = 'FAZER' | 'FEITA' | 'N/A' | '';

export type GuaranteeStatus = 'TO DO' | 'EM ANDAMENTO' | 'CONFERIDO' | 'CONCLUIDO' | 'QUITADA' | 'QUITADA PARCIAL' | 'PAUSADO' | 'ACE' | 'N/A';
export type TraderStatus = 'CONCLUIDO' | 'AGUARDANDO DOC - Área Informada' | 'TEM PENDÊNCIA' | 'N/A';
export type GuaranteeValidationStatus = 'TO DO' | 'EM ANDAMENTO' | 'CONCLUÍDO' | 'PARCIALMENTE VALIDADA' | 'PAUSADO' | 'N/A';
export type OperationType = 'CPRF' | 'CPRF-ADIT' | 'CESSÃO' | 'CESSAO-CD' | 'CD' | 'FLOW';
export type ObligationType = 'COM REGRESSO' | 'SEM REGRESSO' | 'SEM GARANTIA' | '';
export type AnalysisCriteria = 'Completa' | 'Amostragem' | 'N/A' | '';
export type AnalysisIntensity = 'BAIXA' | 'MÉDIA' | 'ALTA' | 'CAÓTICA' | '';

export interface GuaranteeItem {
    id: number;
    operationCode: string;
    operationType: OperationType;
    assignor: string;
    assignorCnpj: string;
    assignorRisk: number;
    assignorRiskPercentage: number;
    mainDebtors: string;
    contractType: string;
    issueDate: string;
    obligation: ObligationType;
    analysisIntensity: AnalysisIntensity;
    statusGuarantee: GuaranteeStatus;
    statusTrader: TraderStatus;
    statusValidation: GuaranteeValidationStatus;
    collateralSaved: boolean;
    totalTitles: number;
    totalAmount: number;
    docsOkStatus: string;
    analysisCriteria: AnalysisCriteria;
    sampledTitles: number;
    notAnalyzed: number;
    // Document checklist
    xml: DocStatus;
    xmlArchived: number;
    xmlPending: number;
    nfPdf: DocStatus;
    nfPdfArchived: number;
    nfPdfPending: number;
    canhoto: DocStatus;
    canhotoArchived: number;
    canhotoPending: number;
    duplicata: DocStatus;
    duplicataArchived: number;
    duplicataPending: number;
    callValidation: DocStatus;
    callConfirmed: number;
    callPending: number;
    purchaseContract: DocStatus;
    sefaz: SefazStatus;
    // Additional
    additionalGuarantees: boolean;
    additionalGuaranteeType: string;
    additionalGuaranteeValue: number;
    additionalGuaranteeDocsOk: string;
    debtorNotification: string;
    checkDetail: string;
    analystName: string;
    traderName: string;
    validationAnalystName: string;
    pendency: string;
    observation: string;
    courtRegistration: boolean;
    contractToRegister: string;
    traderHandoverDate: string;
    followUpAreas: string;
}

// --- VALIDATION (Controle de Sacados) ---

export type ValidationStatus = 'VALIDATED' | 'PARTIAL' | 'PENDING' | 'STARTED' | 'NOT_STARTED' | 'IN_VALIDATION' | 'FULLY_VALIDATED' | 'PARTIALLY_VALIDATED';

export interface ValidationItem {
    id: number;
    operationId: string;
    assignorCnpj: string;
    assignor: string;
    amount: number;
    amountPayed: number;
    balance: number;
    percentagePayed: number;
    percentageValidated: number;
    totalTitulos: number;
    totalPayer: number;
    status: ValidationStatus;
    detail: string[];
    observation: string;
}

// --- RECEIVABLE (Título individual) ---

export type ReceivableStatus = 'VALIDADO' | 'E-MAIL ENVIADO' | 'WHATSAPP' | 'VALIDADO VIA WHATSAPP' | 'NÃO CONFIRMA' | 'NÃO RESPONDE' | 'PENDENTE';

export type TitleDocStatus = 'TEM' | 'NÃO TEM' | 'QUITADA' | 'N/A' | '';

export interface GuaranteeTitleItem {
    id: number;
    guaranteeId: number;
    nfe: string;
    titleNumber: string;
    debtorDocument: string;
    debtorName: string;
    city: string;
    state: string;
    issueDate: string;
    dueDate: string;
    amount: number;
    statusDocs: string;
    xml: TitleDocStatus;
    nfPdf: TitleDocStatus;
    canhoto: TitleDocStatus;
    canhotoSituation: string;
    callValidation: TitleDocStatus;
    duplicataAceite: TitleDocStatus;
    duplicataSemAceite: TitleDocStatus;
    purchaseContract: TitleDocStatus;
    sefaz: TitleDocStatus;
    observation: string;
}

export interface ReceivableItem {
    id: number;
    validationId: number;
    debtor: string;
    debtorDocument: string;
    branch: string;
    city: string;
    state: string;
    titleNumber: string;
    issueDate: string;
    dueDate: string;
    amount: number;
    amountValidated: number;
    status: ReceivableStatus;
    contact: string;
    email: string;
    observation: string;
}

// Status colors
export const getGuaranteeStatusColor = (status: GuaranteeStatus): string => {
    const map: Record<string, string> = {
        'TO DO': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
        'EM ANDAMENTO': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
        'CONFERIDO': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
        'CONCLUIDO': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
        'QUITADA': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
        'QUITADA PARCIAL': 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
        'PAUSADO': 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
        'ACE': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
        'N/A': 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-500',
    };
    return map[status] || map['N/A'];
};

export const getValidationStatusColor = (status: ValidationStatus): string => {
    const map: Record<string, string> = {
        VALIDATED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
        FULLY_VALIDATED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
        '100% Validada': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
        PARTIAL: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
        PARTIALLY_VALIDATED: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
        'Parcialmente validada': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
        PENDING: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
        NOT_STARTED: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
        'Não iniciada': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
        STARTED: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
        IN_VALIDATION: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
        'Em validação': 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
    };
    return map[status] || map.NOT_STARTED;
};

export const getValidationStatusLabel = (status: ValidationStatus): string => {
    const map: Record<string, string> = {
        VALIDATED: '100% Validada',
        FULLY_VALIDATED: '100% Validada',
        PARTIAL: 'Parcialmente Validada',
        PARTIALLY_VALIDATED: 'Parcialmente Validada',
        PENDING: 'Não ligar / Aguardando liberação',
        NOT_STARTED: 'Não Iniciada',
        STARTED: 'Validação Iniciada',
        IN_VALIDATION: 'Em Validação',
    };
    return map[status] || status;
};

export const getReceivableStatusColor = (status: ReceivableStatus): string => {
    const map: Record<string, string> = {
        'VALIDADO': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
        'E-MAIL ENVIADO': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
        'WHATSAPP': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
        'VALIDADO VIA WHATSAPP': 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
        'NÃO CONFIRMA': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
        'NÃO RESPONDE': 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
        'PENDENTE': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };
    return map[status] || map['PENDENTE'];
};

export const getDocStatusColor = (status: DocStatus | SefazStatus): string => {
    const map: Record<string, string> = {
        'OBRIGATORIO': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
        'SIM': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
        'SIM, ANTES': 'bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-400',
        'NÃO': 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
        'CONTRATO': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
        'FAZER': 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
        'FEITA': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
        'N/A': 'bg-gray-50 text-gray-400 dark:bg-gray-800 dark:text-gray-600',
    };
    return map[status] || map['N/A'];
};

// Intensity calculation (from spreadsheet)
export const calculateIntensity = (docs: { xml: boolean; nf: boolean; canhoto: boolean; duplicata: boolean }, titleCount: number): { score: number; level: AnalysisIntensity } => {
    let docScore = 0;
    if (docs.xml) docScore += 1;
    if (docs.nf) docScore += 2;
    if (docs.canhoto) docScore += 4;
    if (docs.duplicata) docScore += 5;

    let titleScore = 0;
    if (titleCount <= 50) titleScore = 1;
    else if (titleCount <= 100) titleScore = 2;
    else if (titleCount <= 150) titleScore = 4;
    else titleScore = 5;

    const total = docScore + titleScore;
    let level: AnalysisIntensity = '';
    if (total <= 4) level = 'BAIXA';
    else if (total <= 8) level = 'MÉDIA';
    else if (total <= 13) level = 'ALTA';
    else level = 'CAÓTICA';

    return { score: total, level };
};

export const getIntensityColor = (level: AnalysisIntensity): string => {
    const map: Record<string, string> = {
        'BAIXA': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
        'MÉDIA': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
        'ALTA': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
        'CAÓTICA': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    };
    return map[level] || 'bg-gray-100 text-gray-800';
};
